import { useCallback, useEffect, useState } from "react";
import { defectsApi } from "../api/defectsApi";
import {
  buildDefectsQueryParams,
  buildDefectCountParams as buildDefectCountParamsForTab,
  buildStatusCountsParams,
} from "../services/defectsQuery";

const EMPTY_STATUS_SUMMARY = { status_counts: {}, total: 0, open: 0, critical_open: 0 };

export function useDefectsData({ viewTab, range, customRange, userId, showToast, teamFilters, appliedFilters, search }) {
  const [defects, setDefects] = useState([]);
  const [optionsDefects, setOptionsDefects] = useState([]);
  const [listTotal, setListTotal] = useState(0);
  const [knownCounts, setKnownCounts] = useState({ issues: 0, mine: 0, external: 0 });
  const [tabCounts, setTabCounts] = useState({ issues: 0, mine: 0, external: 0 });
  const [statusSummary, setStatusSummary] = useState(EMPTY_STATUS_SUMMARY);
  const [statusSummaryLoading, setStatusSummaryLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState("—");

  const fetchTabCount = useCallback(async (tabKey) => {
    const params = buildDefectCountParamsForTab({ tabKey, userId, teamFilters, appliedFilters });
    if (!params) return;
    try {
      const data = await defectsApi.count(params);
      const next = Number(data?.count || 0);
      setTabCounts((prev) => ({ ...prev, [tabKey]: Number.isFinite(next) ? next : 0 }));
    } catch {
      // Keep current count on failures; avoid noisy toasts for badge-only calls.
    }
  }, [userId, teamFilters, appliedFilters]);

  const refreshIssueCounts = useCallback(() => {
    if (viewTab !== "dashboard") return;
    fetchTabCount("issues");
    fetchTabCount("mine");
    fetchTabCount("external");
  }, [viewTab, fetchTabCount]);

  const fetchDefectsForRange = useCallback(async ({ resetList = false } = {}) => {
    const query = buildDefectsQueryParams({
      viewTab,
      range,
      customRange,
      userId,
      teamFilters,
      appliedFilters,
      search,
    });
    if (!query) return;
    setLoading(true);
    if (resetList) setDefects([]);
    try {
      const data = query.mode === "dashboard"
        ? await defectsApi.dashboard(query.params)
        : await defectsApi.list(query.params);
      if (query.mode === "dashboard") {
        setDefects(Array.isArray(data) ? data : []);
      } else {
        const items = Array.isArray(data) ? data : (data?.items || []);
        const total = Number.isFinite(data?.total) ? data.total : items.length;
        setDefects(items);
        setListTotal(total);
        if (viewTab === "issues" || viewTab === "mine" || viewTab === "external") {
          setKnownCounts((prev) => ({ ...prev, [viewTab]: total }));
        }
      }
      setLastUpdated(new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }));
    } catch (err) {
      showToast?.(err.message || "Failed to load defects", "error");
    } finally {
      setLoading(false);
    }
  }, [viewTab, range, customRange, userId, showToast, teamFilters, appliedFilters, search]);

  // Backend-computed per-status counts for the Dashboard's Lifecycle bar +
  // header KPI — see GET /defects/status-counts's docstring for why this
  // replaced a client-side tally over the dashboard-data snapshot (the two
  // could silently disagree with what clicking a segment showed in the
  // Issues tab). Fetched fresh whenever the Dashboard tab is active and
  // whenever range/customRange changes — same triggers as fetchDefectsForRange.
  const fetchStatusSummary = useCallback(async () => {
    if (viewTab !== "dashboard") return;
    setStatusSummaryLoading(true);
    try {
      const data = await defectsApi.statusCounts(buildStatusCountsParams({ range, customRange }));
      setStatusSummary({
        status_counts: data?.status_counts || {},
        total: Number(data?.total) || 0,
        open: Number(data?.open) || 0,
        critical_open: Number(data?.critical_open) || 0,
      });
    } catch (err) {
      showToast?.(err.message || "Failed to load status counts", "error");
    } finally {
      setStatusSummaryLoading(false);
    }
  }, [viewTab, range, customRange, showToast]);

  // Separate, deliberately unfiltered-by-appliedFilters fetch used only to
  // build the dynamic choice-filter dropdowns (Owner/Reporter/Project/
  // Customer/Type — see DefectsViewV2Page.jsx's `fields` useMemo). Sourcing
  // those options from the same server-filtered `defects` result created a
  // self-narrowing bug: once a field's own filter was applied, `defects`
  // shrank to match it, so that field's own option list then only ever
  // showed the value(s) already selected — the user couldn't get back to
  // the full list without clearing every filter to force an unfiltered
  // refetch. This fetch shares the same base scope (tab/date-range/team/
  // status — status has static options so excluding it here doesn't lose
  // anything) but never applies the 5 dynamic choice filters, so their
  // option lists always reflect the full universe of values for the
  // current tab, independent of what's currently selected in any of them.
  const fetchOptionsDefects = useCallback(async () => {
    const query = buildDefectsQueryParams({
      viewTab, range, customRange, userId, teamFilters, appliedFilters: {},
    });
    if (!query || query.mode === "dashboard") return;
    try {
      const data = await defectsApi.list({ ...query.params, limit: 1000 });
      const items = Array.isArray(data) ? data : (data?.items || []);
      setOptionsDefects(items);
    } catch {
      // Options list degrades gracefully — the main defects fetch/table is unaffected.
    }
  }, [viewTab, range, customRange, userId, teamFilters]);

  useEffect(() => {
    fetchDefectsForRange();
  }, [fetchDefectsForRange]);

  useEffect(() => {
    fetchOptionsDefects();
  }, [fetchOptionsDefects]);

  useEffect(() => {
    refreshIssueCounts();
  }, [refreshIssueCounts]);

  useEffect(() => {
    fetchStatusSummary();
  }, [fetchStatusSummary]);

  return {
    defects,
    setDefects,
    optionsDefects,
    listTotal,
    knownCounts,
    tabCounts,
    statusSummary,
    statusSummaryLoading,
    loading,
    lastUpdated,
    fetchTabCount,
    refreshIssueCounts,
    fetchDefectsForRange,
    fetchStatusSummary,
  };
}
